import { action, computed } from '@ember/object';
import { inject as service } from '@ember/service';
import Controller from '@ember/controller';
import { copy } from 'ember-copy';
import { timeout } from 'ember-concurrency';
import { task } from 'ember-concurrency';
import AttributesProxy from 'cio/app-models/customer/attributes-proxy';
import config from 'cio/config/environment';
import { areObjectsEqual } from 'cio/utils/objects';
import AlertsService from 'cio/services/alerts';
import ApiErrorsService from 'cio/services/api-errors';
import EnvironmentService from 'cio/services/environment';
import { ApiResponse, ApiError } from 'cio/services/api';
import { CREATED_IN_CIO_AT, PERMANENT_ATTRIBUTES } from 'cio/utils/attributes';
import { IdentifierType, IdentifierKey } from 'cio/utils/identifiers';
import EventBusService, { CUSTOMER_ATTRIBUTES_UPDATED } from 'cio/services/event-bus';
import DS from 'ember-data';
import RouterService from '@ember/routing/router-service';
import CustomerModel from 'cio/models/customer';

export default class EditController extends Controller {
  @service alerts!: AlertsService;
  @service apiErrors!: ApiErrorsService;
  @service environment!: EnvironmentService;
  @service eventBus!: EventBusService;
  @service store!: DS.Store;
  @service router!: RouterService;

  declare model: CustomerModel;

  interval = config.APP.peopleDelay;
  attributesProxy: AttributesProxy | null = null;
  isNew = false;

  @computed('attributesProxy.isDirty')
  get isDirty() {
    return this.attributesProxy?.isDirty ?? false;
  }

  prepareAttributesProxy() {
    let attrs = this.model.get('filteredAttrsWithIdentifiers') as Record<string, string>[];

    // Add email to the attributes list to be consistent with classic ID based
    // workspace behavior
    if (
      this.environment.current.canIdentifyById &&
      !this.environment.current.canIdentifyByEmail &&
      !(IdentifierKey.Email in this.model.get('attrs'))
    ) {
      // insert after cio_id (if it exists) and id
      attrs = attrs.reduce((acc, attr) => {
        if (IdentifierKey.Id in attr) {
          return [...acc, attr, { email: '' }];
        }

        return [...acc, attr];
      }, []);
    }

    this.set(
      'attributesProxy',
      AttributesProxy.create({
        attrs,
        requiredAttributes: this.environment.current.enabledIdentifiers,
        permanentAttributes:
          this.environment.current.identifierType !== IdentifierType.MultiIdentifier ? PERMANENT_ATTRIBUTES : undefined,
        autoGeneratedAttributes:
          this.environment.current.identifierType === IdentifierType.Email
            ? [IdentifierKey.CioId, IdentifierKey.Id, CREATED_IN_CIO_AT]
            : [IdentifierKey.CioId, CREATED_IN_CIO_AT],
        isNew: this.isNew,
      }),
    );
  }

  // Since customer updates are async, we don't get immediate feedback
  // on whether and when the change actually gets persisted.
  // We issue a GET request after a timeout to check if the customer
  // has been updated yet.
  isCustomerUpdated = task(async () => {
    let targetAttributes = copy(this.model.attrs);

    this.alerts.sticky('person.update', 'Updating your customer…');

    await timeout(this.interval);

    try {
      let apiCustomer = await this.store.findRecord('customer', this.model.id);

      if (areObjectsEqual(targetAttributes, apiCustomer.attrs)) {
        this.alerts.remove('person.update');
      } else {
        this.alerts.warning('person.update', 'Your customer will be updated soon.');
      }

      this.model.rollbackAttributes();
      this.attributesProxy?.reset();
      this.router.transitionTo('person.attributes.index', this.model.id);

      this.eventBus.publish(CUSTOMER_ATTRIBUTES_UPDATED);
    } catch (e) {
      this.alerts.remove('person.update');

      throw e;
    }
  });

  save = task(async (attrs: Record<string, any>, hiddenAttrs: string[]) => {
    this.model.setProperties({
      attrs,
      hiddenAttrs,
    });

    try {
      await this.model.save();
    } catch (err) {
      this.model.rollbackAttributes();
      return this.processServerError(err);
    }

    this.model.updateSentAttributes(attrs);

    await this.isCustomerUpdated.perform();
  });

  @action
  reset() {
    this.model.rollbackAttributes();
    this.router.transitionTo('person.attributes.index', this.model.id);
  }

  @action
  processServerError(apiError: ApiResponse) {
    let error: Record<string, any> = (apiError.errors as ApiError[] | undefined)?.[0] ?? {};

    if (error.status === '400') {
      if (error.detail?.startsWith('A person with this email')) {
        this.attributesProxy?.findAttrByKey('email')?.set('serverError', error.detail);

        return;
      } else if (error.detail?.startsWith('A person with this id')) {
        this.attributesProxy?.findAttrByKey('id')?.set('serverError', error.detail);

        return;
      }
    } else if (error.source != null) {
      let attribute = error.source?.pointer?.replace('/data/attributes/', '');

      this.attributesProxy?.findAttrByKey(attribute)?.set('serverError', error.detail);

      return;
    }

    this.apiErrors.report(apiError);
  }
}
